import {
  DataSnapshot,
  HistoricalIndex,
  WeeklyDigest,
  LatencySnapshot,
  LatencyDailyIndex,
  ValidationSnapshot,
} from '@bedrock-profiles/shared';
import { DEFAULT_DEV_OPENAI_MOCK_LIMIT, injectOpenAIMockData } from '../data/openAIMockData';

const DATA_BASE_URL = import.meta.env.VITE_DATA_URL || '/data';

interface SnapshotResponse {
  ok: boolean;
  json: () => Promise<DataSnapshot>;
  statusText: string;
}

function looksLikeDataSnapshot(value: unknown): value is DataSnapshot {
  if (!value || typeof value !== 'object') return false;
  const cast = value as Partial<DataSnapshot>;
  return typeof cast.timestamp === 'string' && typeof cast.version === 'string';
}

async function tryParseSnapshotResponse(response: SnapshotResponse): Promise<DataSnapshot | null> {
  try {
    const parsed = await response.json();
    return looksLikeDataSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function applyDevMockData(snapshot: DataSnapshot): DataSnapshot {
  if (import.meta.env.DEV && !import.meta.env.VITEST) {
    const parsedLimit = Number.parseInt(import.meta.env.VITE_DEV_OPENAI_MOCK_LIMIT ?? '', 10);
    const maxGeneratedOfferings = Number.isFinite(parsedLimit) && parsedLimit > 0
      ? parsedLimit
      : DEFAULT_DEV_OPENAI_MOCK_LIMIT;

    return injectOpenAIMockData(snapshot, { maxGeneratedOfferings });
  }
  return snapshot;
}

export function isExpectedSnapshotFetchError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch snapshot') &&
    (message.includes('network error') ||
      message.includes('invalid snapshot response') ||
      message.includes('Not Found'))
  );
}

async function fetchSnapshotByUrl(url: string): Promise<SnapshotResponse> {
  return fetch(url) as Promise<SnapshotResponse>;
}

async function tryFetchSnapshotByUrl(url: string): Promise<SnapshotResponse | null> {
  try {
    return await fetchSnapshotByUrl(url);
  } catch {
    return null;
  }
}

/**
 * Fetch the historical index of all available snapshots
 */
export async function fetchHistoricalIndex(): Promise<HistoricalIndex> {
  const response = await fetch(`${DATA_BASE_URL}/index.json`);

  if (!response.ok) {
    throw new Error(`Failed to fetch historical index: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchWeeklyDigest(issueDate?: string): Promise<WeeklyDigest> {
  const weeklyPath =
    issueDate && issueDate !== 'latest'
      ? `${DATA_BASE_URL}/weekly/issues/${issueDate}.json`
      : `${DATA_BASE_URL}/weekly/latest.json`;

  const requestUrl = import.meta.env.DEV
    ? `${weeklyPath}${weeklyPath.includes('?') ? '&' : '?'}t=${Date.now()}`
    : weeklyPath;

  const response = await fetch(requestUrl, { cache: 'no-store' });
  if (!response?.ok) {
    throw new Error(`Failed to fetch weekly digest: ${response?.statusText || 'network error'}`);
  }

  return response.json();
}

/**
 * Fetch latest complete catalog snapshot (may be unavailable if no complete run exists yet)
 */
export async function fetchCatalogLatestCompleteSnapshot(): Promise<DataSnapshot | null> {
  const response = await tryFetchSnapshotByUrl(`${DATA_BASE_URL}/catalog/latest-complete.json`);
  if (!response?.ok) {
    return null;
  }

  const snapshot = await tryParseSnapshotResponse(response);
  return snapshot ? applyDevMockData(snapshot) : null;
}

/**
 * Fetch a specific data snapshot
 */
export async function fetchSnapshot(
  version: string,
  snapshotUrl?: string
): Promise<DataSnapshot> {
  const indexedUrl = snapshotUrl?.startsWith('/data/')
    ? `${DATA_BASE_URL}${snapshotUrl.slice('/data'.length)}`
    : snapshotUrl;
  const urlsToTry =
    version === 'latest'
      ? [`${DATA_BASE_URL}/latest.json`, `${DATA_BASE_URL}/catalog/latest.json`]
      : [indexedUrl ?? `${DATA_BASE_URL}/snapshots/${version}.json`];

  let response: SnapshotResponse | null = null;
  for (const candidateUrl of urlsToTry) {
    response = await tryFetchSnapshotByUrl(candidateUrl);
    if (!response?.ok) {
      continue;
    }

    const parsedSnapshot = await tryParseSnapshotResponse(response);
    if (parsedSnapshot) {
      return applyDevMockData(parsedSnapshot);
    }
  }

  // Local/dev fallback when latest.json is unavailable:
  // 1) use index.latestVersion
  // 2) use first snapshot entry from index
  if (version === 'latest') {
    try {
      const index = await fetchHistoricalIndex();
      const fallbackVersion = index.latestVersion || index.snapshots?.[0]?.date;

      if (fallbackVersion) {
        const fallbackResponse = await tryFetchSnapshotByUrl(`${DATA_BASE_URL}/snapshots/${fallbackVersion}.json`);
        if (fallbackResponse?.ok) {
          const snapshot = await tryParseSnapshotResponse(fallbackResponse);
          if (snapshot) {
            return applyDevMockData(snapshot);
          }
        }
      }
    } catch {
      // no-op, keep fallback chain moving
    }

  }

  if (!response?.ok) {
    throw new Error(`Failed to fetch snapshot ${version}: ${response?.statusText || 'network error'}`);
  }

  const snapshot = await tryParseSnapshotResponse(response);
  if (!snapshot) {
    throw new Error(`Failed to fetch snapshot ${version}: invalid snapshot response`);
  }
  return applyDevMockData(snapshot);
}

// In-flight request cache to prevent duplicate simultaneous requests
const inFlightRequests = new Map<string, Promise<unknown>>();

/**
 * Fetch latest latency probe data with request deduplication
 */
export async function fetchLatencyData(): Promise<LatencySnapshot> {
  const url = `${DATA_BASE_URL}/latency/latest.json`;

  // Check if there's already an in-flight request for this URL
  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url) as Promise<LatencySnapshot>;
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch latency data: ${response.statusText}`);
      }

      return response.json();
    } finally {
      // Clean up the in-flight request after completion
      inFlightRequests.delete(url);
    }
  })();

  // Store the in-flight request
  inFlightRequests.set(url, fetchPromise);

  return fetchPromise;
}

export async function fetchLatencyDailyIndex(): Promise<LatencyDailyIndex> {
  const url = `${DATA_BASE_URL}/latency/daily/index.json`;

  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url) as Promise<LatencyDailyIndex>;
  }

  const fetchPromise = (async () => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch latency history index: ${response.statusText}`);
      }

      return response.json();
    } finally {
      inFlightRequests.delete(url);
    }
  })();

  inFlightRequests.set(url, fetchPromise);
  return fetchPromise;
}

export async function fetchLatencySnapshotByUrl(url: string): Promise<LatencySnapshot> {
  const resolvedUrl = url.startsWith('http') || url.startsWith('/')
    ? url
    : `${DATA_BASE_URL}/${url.replace(/^\/+/, '')}`;

  if (inFlightRequests.has(resolvedUrl)) {
    return inFlightRequests.get(resolvedUrl) as Promise<LatencySnapshot>;
  }

  const fetchPromise = (async () => {
    try {
      const response = await fetch(resolvedUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch latency snapshot: ${response.statusText}`);
      }

      return response.json();
    } finally {
      inFlightRequests.delete(resolvedUrl);
    }
  })();

  inFlightRequests.set(resolvedUrl, fetchPromise);
  return fetchPromise;
}

/**
 * Fetch latest validation snapshot data with request deduplication
 */
export async function fetchValidationData(): Promise<ValidationSnapshot> {
  const url = `${DATA_BASE_URL}/validation/latest.json`;

  // Check if there's already an in-flight request for this URL
  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url) as Promise<ValidationSnapshot>;
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch validation data: ${response.statusText}`);
      }

      return response.json();
    } finally {
      // Clean up the in-flight request after completion
      inFlightRequests.delete(url);
    }
  })();

  // Store the in-flight request
  inFlightRequests.set(url, fetchPromise);

  return fetchPromise;
}

/**
 * Fetch pre-computed timeline file for an offering
 */
export async function fetchTimeline(offeringId: string): Promise<import('@bedrock-profiles/shared').TimelineFile> {
  // Strip :0 suffix to match timeline file naming
  const filename = offeringId.replace(/:\d+$/, '');
  const response = await fetch(`${DATA_BASE_URL}/timelines/${encodeURIComponent(filename)}.json`);

  if (!response.ok) {
    throw new Error(`Failed to fetch timeline: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get cached snapshot from IndexedDB
 * Falls back to localStorage for backwards compatibility during transition
 */
function getCacheKey(version: string): string {
  return `snapshot-${version}`;
}

export async function getCachedSnapshot(version: string): Promise<DataSnapshot | null> {
  // Try IndexedDB first
  try {
    const { getCachedSnapshot: getFromIndexedDB } = await import('./indexedDBCache');
    const fromDB = await getFromIndexedDB<DataSnapshot>(version);
    if (fromDB) return fromDB;
  } catch {
    // IndexedDB not available, fall through to localStorage
  }

  // Fallback to localStorage for backwards compatibility
  try {
    const cached = localStorage.getItem(getCacheKey(version));
    if (!cached) return null;
    const parsed: unknown = JSON.parse(cached);
    return looksLikeDataSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Cache snapshot in IndexedDB
 * Also attempts localStorage as fallback for small snapshots
 */
export async function cacheSnapshot(version: string, snapshot: DataSnapshot): Promise<void> {
  // Try IndexedDB first (handles large data)
  try {
    const { cacheSnapshot: saveToIndexedDB } = await import('./indexedDBCache');
    await saveToIndexedDB(version, snapshot, snapshot.version);
    return;
  } catch (err) {
    console.warn('[IndexedDB] Failed to cache, falling back to localStorage:', err);
  }

  // Fallback to localStorage for small snapshots
  try {
    localStorage.setItem(getCacheKey(version), JSON.stringify(snapshot));
  } catch (err) {
    console.warn('[localStorage] Failed to cache snapshot (quota exceeded):', err);
  }
}
